import { useEffect } from 'react';
import {
  Box,
  TableContainer,
  Table,
  TableHead,
  TableCell,
  Paper,
  TableBody,
  TableRow,
  Typography,
  CircularProgress,
} from '@mui/material';
import { colors } from '../constants/globalcss';
import { format } from 'date-fns';
import { extractFileNames } from '../utils/fileUtils';
import { useRequests } from '../contexts/RequestsContext';
import { SearchOptions } from '../constants/app';

export default function RequestsList({
  searchText,
  selectedRequestType,
  selectedTimePeriod,
}: SearchOptions) {
  const { requests, loading, error, fetchRequests } = useRequests();
  const COLUMN_HEADERS = [
    'Servicer Account Id',
    'Type',
    'Submitted By',
    'Submitted Date',
    'Uploaded Files',
  ];

  useEffect(() => {
    fetchRequests({ searchText, selectedRequestType, selectedTimePeriod });
  }, [fetchRequests, searchText, selectedRequestType, selectedTimePeriod]);

  if (error) {
    return (
      <Box sx={{ color: 'error.main', my: 4, textAlign: 'center' }}>
        <Typography variant="body1">{error}</Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: colors.background.paper,
        pb: 2.5,
        pt: 1.5,
        px: 2.5,
        width: '100%',
      }}
    >
      {requests.length > 0 ? (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            '& .MuiTableHead-root': {
              backgroundColor: colors.background.paper,
              position: 'sticky',
              top: 0,
              zIndex: 1,
            },
            border: '1px solid rgba(22, 56, 96, 0.12)',
            borderRadius: 1,
            boxShadow: 'none',
            flex: 1,
            mb: 0,
            minHeight: 240,
            overflow: 'auto',
            width: '100%',
          }}
        >
          <Table
            stickyHeader
            size="small"
            sx={{ tableLayout: 'fixed', width: '100%' }}
          >
            <TableHead>
              <TableRow>
                {COLUMN_HEADERS.map((header) => (
                  <TableCell
                    key={header}
                    sx={{
                      backgroundColor: colors.primary,
                      color: colors.background.paper,
                      fontWeight: 600,
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((request) => (
                <TableRow key={request.created_at} hover>
                  <TableCell sx={{ minWidth: 100, whiteSpace: 'nowrap' }}>
                    {request.servicer_account_id}
                  </TableCell>
                  <TableCell sx={{ minWidth: 100, whiteSpace: 'nowrap' }}>
                    {request.request_type}
                  </TableCell>
                  <TableCell sx={{ minWidth: 100, whiteSpace: 'nowrap' }}>
                    {request.submitter_name}
                  </TableCell>
                  <TableCell sx={{ minWidth: 100, whiteSpace: 'nowrap' }}>
                    {format(new Date(request.created_at), 'MM/dd/yyyy hh:mm a')}
                  </TableCell>
                  <TableCell
                    sx={{
                      maxHeight: 140,
                      overflowY: 'auto',
                      verticalAlign: 'top',
                    }}
                  >
                    <Box
                      component="ul"
                      sx={{ listStyle: 'none', m: 0, p: 0, pl: 0 }}
                    >
                      {extractFileNames(request.uploaded_files).map(
                        (fileName, index) => (
                          <Box component="li" key={index} sx={{ py: 0.25 }}>
                            {fileName}
                          </Box>
                        )
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography variant="h6" textAlign="center" color="text.secondary">
          No request history found for the selected criteria. Please adjust your
          filters and try again.
        </Typography>
      )}
    </Box>
  );
}
